import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { ButtonHTMLAttributes, HTMLAttributes, forwardRef } from "react";

import LoadingSpinner from "../LoadingSpinner";

import { cn } from "@/lib/utils";

export type ButtonSize =
  | "default"
  | "xs"
  | "sm"
  | "lg"
  | "xl"
  | "icon"
  | "tinyIcon"
  | "tinyButton"
  | "pill";

const buttonVariants = cva(
  "inline-flex transition-all duration-300 items-center justify-center rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 transition-transform",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent text-gray-800 hover:text-gray-600",
        destructiveOutline:
          "border border-red-300 bg-background shadow-sm hover:bg-red-50 text-destructive hover:text-red-600",
        secondaryOutline:
          "border border-blue-300 bg-background shadow-sm hover:bg-blue-50 text-gray-800 hover:text-blue-600",
        blueOutline:
          "border border-blue-500 bg-background shadow-sm hover:bg-blue-50 text-blue-600 hover:text-blue-700",
        secondary:
          "bg-gray-50 border border-gray-300 text-secondary-foreground shadow-sm hover:bg-secondary/80",
        secondaryLight:
          "bg-blue-100 text-blue-700 shadow-sm hover:bg-blue-100/80 hover:text-blue-600",
        secondaryLightOutline:
          "border border-blue-300 bg-blue-50 text-blue-600 shadow-sm hover:bg-blue-50/80 hover:border-blue-400",
        ghost: "hover:bg-black/5 hover:text-gray-600 text-gray-800",
        ghostGray: "bg-black/5 hover:bg-black/10 text-gray-800 hover:text-gray-900",
        ghostBlue: "text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700",
        link: "text-blue-600 hover:text-blue-400 underline-offset-4",
        inlineLink: "text-sm font-semibold text-[#1D4ED8] hover:text-[#1D4ED8]/90 !p-0",
        destructiveLink: "text-red-600 hover:text-red-400 underline-offset-4",
        blue: "bg-blue-100 text-blue-700 hover:bg-blue-200",
        pill: "border border-gray-200 bg-white text-gray-400 shadow-sm transition-colors duration-150 hover:border-gray-300 hover:text-gray-600 hover:shadow focus-visible:ring-2 focus-visible:ring-gray-300 focus-visible:ring-offset-1",
      },
      size: {
        default: "px-4 py-2",
        xs: "px-2 py-1.5 text-xs",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        xl: "h-12 rounded-lg px-10 text-lg",
        icon: "h-9 w-9",
        tinyIcon: "h-7 w-7",
        tinyButton: "h-5 px-2 text-xs",
        pill: "rounded-full px-2.5 py-0.5 text-xs gap-1",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ExtendedButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof buttonVariants>;

export interface ButtonProps extends ExtendedButtonProps {
  asChild?: boolean;
  isLoading?: boolean;
  size?: ButtonSize;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, disabled, size, variant, asChild = false, isLoading = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={disabled || isLoading}
        ref={ref}
        {...props}
      >
        {isLoading ? <LoadingSpinner size={5} /> : props.children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

const DivButton = forwardRef<HTMLDivElement, ButtonProps>(
  ({ className, disabled, size, variant, asChild = false, isLoading = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "div";
    const isDisabled = disabled || isLoading;
    return (
      <Comp
        className={cn(
          "cursor-pointer",
          buttonVariants({ variant, size, className }),
          isDisabled && "pointer-events-none opacity-50",
        )}
        aria-disabled={isDisabled}
        ref={ref}
        {...props}
      >
        {isLoading ? <LoadingSpinner size={5} /> : props.children}
      </Comp>
    );
  },
);
DivButton.displayName = "DivButton";

export { Button, DivButton, buttonVariants };
